import { isInvoiceCreditMemo } from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { formatCurrency } from '../lib/formatCurrency';
import { formatStatementDate } from '../lib/formatStatementDate';
import * as portalInvoiceService from '../services/portalInvoiceService';
import { CardChevron } from './CardChevron';
import { InvoicePaymentStatusPill } from './InvoiceStatusChips';
import { RecentListRowsSkeleton } from './skeletons';

/**
 * Account-home recent invoices (PD-14080 review feedback): latest three
 * with a View all link — middle column of the recent-activity row.
 */
export function RecentInvoicesCard({
	title,
}: {
	title?: string;
} = {}): ReactElement | null {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const display = useDisplaySettings();
	const { token, activeClientId } = usePortalAuth();

	const { data, isLoading } = useQuery({
		// Key on the active client so the card refetches on an account switch.
		queryKey: ['portal-invoices-recent', activeClientId],
		queryFn: () =>
			portalInvoiceService.getInvoices(token ?? '', { page: 1, limit: 3 }),
		enabled: token != null,
		staleTime: 30_000,
	});

	// PD-14239: invoices are hidden entirely for hide-pricing users (server
	// forces canViewInvoices off). Defensive — the dashboard dispatch already
	// skips mounting this card.
	if (!display.canViewInvoices) {
		return null;
	}

	// Skeleton-first (PD-14692): real header + shaped rows, so the card
	// doesn't pop into an already-settled layout when the data lands.
	// (isLoading is false while the query is disabled, so anon visitors
	// still get the null fall-through below.)
	if (isLoading) {
		return (
			<div>
				<div className="flex items-center justify-between mb-2">
					<span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
						{t(title ?? 'Recent Invoices')}
					</span>
				</div>
				<RecentListRowsSkeleton />
			</div>
		);
	}

	const invoices = data?.data ?? [];
	if (invoices.length === 0) {
		return null;
	}

	return (
		<div>
			<div className="flex items-center justify-between mb-2">
				<span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
					{t(title ?? 'Recent Invoices')}
				</span>
				<button
					type="button"
					onClick={() => {
						void navigate('/invoices');
					}}
					className="text-xs text-portal-accent hover:underline"
				>
					{t('View all')}
				</button>
			</div>
			<div className="flex flex-col gap-2">
				{invoices.map((invoice) => {
					// Same credit-memo detection as the invoices list page — payment
					// status is meaningless for a credit, so the pill is skipped and
					// the label identifies the row instead.
					const isCreditMemo =
						isInvoiceCreditMemo(invoice.type) ||
						(invoice.total != null && parseFloat(invoice.total) < 0);
					return (
						<button
							key={invoice.id}
							type="button"
							onClick={() => {
								void navigate(`/invoices/${invoice.id}`);
							}}
							className="bg-brand-surface border border-brand-border rounded-xl p-3 flex items-start justify-between gap-3 text-left hover:border-portal-accent transition-colors"
						>
							{/* flex-1 pins the total + chevron to the row's right edge
							    (PD-14241 alignment fix — see StatementsPage). */}
							<div className="flex-1 min-w-0">
								<div className="flex items-center gap-2 min-w-0">
									<span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
										{isCreditMemo ? t('Credit Memo') : t('Invoice')} #
										{t(invoice.externalId ?? String(invoice.id))}
									</span>
									{/* PD-14691: the homepage "recent invoices" widget shows
									    status at a glance, matching the invoices list. */}
									{!isCreditMemo && (
										<InvoicePaymentStatusPill invoice={invoice} />
									)}
								</div>
								<div className="text-xs text-brand-muted mt-0.5">
									{formatStatementDate(invoice.date)}
									{invoice.outstandingBalance != null &&
										parseFloat(invoice.outstandingBalance) > 0 && (
											<>
												{' · '}
												{t('Open')} {formatCurrency(invoice.outstandingBalance)}
											</>
										)}
								</div>
							</div>
							<div className="text-sm font-semibold text-gray-900 dark:text-gray-100 shrink-0">
								{formatCurrency(invoice.total)}
							</div>
							<CardChevron />
						</button>
					);
				})}
			</div>
		</div>
	);
}
